// Live discovery: stream list, category rankings, and the user's hidden
// categories. Initial state comes from REST; updates arrive as socket
// `live:event`s routed by live/onEvent.
import { liveApi } from '@/etc/liveApi'
import { HIDEABLE_CATEGORIES } from '@/etc/liveMeters'
import { joinLiveRoom, leaveLiveRoom } from '@/store/live/socket'

const HIDDEN_KEY = 'vyby.live.hiddenCategories'

const loadHidden = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]')
    return Array.isArray(raw) ? raw.filter((k) => HIDEABLE_CATEGORIES.includes(k)) : []
  } catch {
    return []
  }
}

export default {
  namespaced: true,
  state: {
    streams: {},
    rankings: null,
    loading: false,
    error: null,
    joined: false,
    hiddenCategories: loadHidden(),
  },
  getters: {
    liveStreams: (state) =>
      Object.values(state.streams).filter((s) => s.status === 'live' && s.startedAt),
    // Ranked rows with hidden categories removed and stream objects resolved.
    rows: (state) => {
      const rows = state.rankings?.rows || []
      return rows
        .filter((row) => !state.hiddenCategories.includes(row.category))
        .map((row) => ({
          ...row,
          streams: row.streams
            .map((entry) => ({ ...entry, stream: state.streams[entry.streamId] }))
            .filter((entry) => entry.stream && entry.stream.status === 'live'),
        }))
        .filter((row) => row.streams.length)
    },
    isHidden: (state) => (category) => state.hiddenCategories.includes(category),
  },
  mutations: {
    streams: (state, list) => {
      state.streams = Object.fromEntries((list || []).map((s) => [s.streamId, s]))
    },
    upsertStream: (state, stream) => {
      if (!stream?.streamId) return
      state.streams = { ...state.streams, [stream.streamId]: stream }
    },
    rankings: (state, next) => {
      state.rankings = next || null
    },
    loading: (state, next) => {
      state.loading = !!next
    },
    error: (state, next) => {
      state.error = next
    },
    joined: (state, next) => {
      state.joined = !!next
    },
    hiddenCategories: (state, next) => {
      state.hiddenCategories = next
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify(next))
      } catch {
        // storage unavailable — keep in-memory only
      }
    },
  },
  actions: {
    load: async (store) => {
      const jwt = store.rootState.user.jwt
      store.commit('loading', true)
      store.commit('error', null)
      try {
        const [streams, rankings] = await Promise.all([liveApi.streams(jwt), liveApi.rankings(jwt)])
        store.dispatch('receiveSnapshot', { streams: streams.streams, rankings: rankings.rankings })
        return true
      } catch (err) {
        console.error('live discovery load failed', err)
        store.commit('error', err.message || 'LIVE_LOAD_FAILED')
        return false
      } finally {
        store.commit('loading', false)
      }
    },

    receiveSnapshot: (store, { streams = [], rankings = null }) => {
      store.commit('streams', streams)
      if (rankings) store.commit('rankings', rankings)
      streams.forEach((s) => store.dispatch('live/meters/fromStream', s, { root: true }))
    },

    join: async (store) => {
      const reply = await joinLiveRoom(store, { room: 'discovery' }, (ack) => {
        store.dispatch('receiveSnapshot', ack)
      })
      store.commit('joined', !!reply?.success)
      return reply
    },

    leave: async (store) => {
      store.commit('joined', false)
      await leaveLiveRoom(store, { room: 'discovery' })
    },

    toggleCategory: (store, category) => {
      if (!HIDEABLE_CATEGORIES.includes(category)) return
      const hidden = store.state.hiddenCategories
      store.commit(
        'hiddenCategories',
        hidden.includes(category) ? hidden.filter((c) => c !== category) : [...hidden, category]
      )
    },
  },
}
